import React, {useState, useRef, useEffect} from 'react';
import Sun from '../../assets/icons/Sun.svg';
import Edit from '../../assets/icons/Edit.svg';
import Clock from '../../assets/icons/Clock.svg';
import Check from '../../assets/icons/Check.svg';
import Trash from '../../assets/icons/Trash.svg';
import apiClient from '../../context/apiClient';
import PinIcon from "../../assets/icons/Map_pin.svg";
import Plus from "../../assets/icons/Plus.svg";
import Calendar from "../../assets/icons/Calendar.svg";
import Retire from "../../assets/icons/Log_out.svg";
import Employ from "../../assets/icons/Log_in.svg";
import User from "../../assets/icons/User.svg";

const languageFlags = {
    DE: "🇩🇪", EN: "🇬🇧", FR: "🇫🇷", ES: "🇪🇸", IT: "🇮🇹",
    NE: "🇳🇱", PL: "🇵🇱", TR: "🇹🇷", PT: "🇵🇹", RU: "🇷🇺",
    AR: "🇸🇦", ZH: "🇨🇳", JA: "🇯🇵", KO: "🇰🇷", SE: "🇸🇪",
    NO: "🇳🇴", FI: "🇫🇮", DK: "🇩🇰", HU: "🇭🇺", CZ: "🇨🇿",
    SK: "🇸🇰", RO: "🇷🇴", BG: "🇧🇬", HR: "🇭🇷", EL: "🇬🇷",
};

const parseAddressString = (employee) => {
    if (!employee.location || typeof employee.location !== 'string') {
        return {street: '', streetNumber: '', postalCode: '', city: '', country: ''};
    }

    const parts = employee.location.split(',').map(p => p.trim());

    // Grober Fallback, kann je nach Format angepasst werden
    return {
        street: parts[0]?.replace(/\d+$/, '').trim() || '',
        streetNumber: parts[0]?.match(/\d+$/)?.[0] || '',
        city: parts[1] || '',
        postalCode: parts[2] || '',
        country: parts[3] || '',
    };
};

const formatLocalDateTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return new Intl.DateTimeFormat('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(date).replace(',', '');
};


// Diese Map sagt: welche Qualifikationen sind für welche Rolle erlaubt
const allowedQualificationGroupsByRole = {
    INSTRUCTOR: ["KL", null], // null = ohne Suffix
    MECHANIC: ["M"],
    GUEST_MANAGER: ["GM"],
};


const formatAddress = (addressString) => {
    const match = addressString?.match(/street=(.*?), streetNumber=(.*?), city=(.*?), postalCode=(.*?), country=(.*?)\)/);
    if (!match) return addressString;
    const [, street, number, city, postalCode, country] = match;
    return `${street} ${number}\n${postalCode} ${city}\n${country}`;
};

const stringifyLocation = (location) => {
    if (!location) return '';
    const {street, streetNumber, postalCode, city, country} = location;
    return `${street}, ${streetNumber}, ${postalCode}, ${city}, ${country}`;
};


const EmployeeCard = ({employee, onUpdated}) => {
    const [isFlipped, setIsFlipped] = useState(false);
    const [showAddressModal, setShowAddressModal] = useState(false);

    // For the plus dropdown
    const [showLanguageSelect, setShowLanguageSelect] = useState(false);
    const dropdownRef = useRef(null);

    const [showQualificationModal, setShowQualificationModal] = useState(false);
    const [allQualifications, setAllQualifications] = useState([]);
    const [qualificationInput, setQualificationInput] = useState('');
    const [newQualificationSuffix, setNewQualificationSuffix] = useState('');
    const [showVacationModal, setShowVacationModal] = useState(false);
    const [newVacation, setNewVacation] = useState({startTime: '', endTime: ''});
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const [updateError, setUpdateError] = useState('');


    const [editData, setEditData] = useState({
        firstName: employee.firstName,
        lastName: employee.lastName,
        overtime: employee.overtime,
        vacation: employee.vacation,
        monthlyHours: employee.monthlyHours,
        retired: employee.retired,
        blockedTime: employee.blockedTime || [],
        vacationDays: employee.vacationDays || [],
        languages: employee.languages || [],
        qualifications: (employee.qualifications || []).map((q) =>
            typeof q === 'string' ? q : q?.name || ''
        ),
        location: typeof employee.location === 'string'
            ? parseAddressString(employee)
            : employee.location || {
            street: '', streetNumber: '', postalCode: '', city: '', country: ''
        },
        base64Image: employee.base64Image || null,
    });

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onloadend = () => {
            const base64 = reader.result.split(',')[1]; // strip MIME prefix
            setEditData((prev) => ({
                ...prev,
                base64Image: base64,
            }));
        };
        reader.readAsDataURL(file);
    };


    useEffect(() => {
        const fetchQualifications = async () => {
            try {
                const res = await apiClient.get('/api/utility/allQualifications');
                if (res.status === 200) {
                    const names = res.data.map(q => q.name);
                    setAllQualifications(names);
                }
            } catch (err) {
                console.error("Fehler beim Laden der Qualifikationen", err);
            }
        };

        fetchQualifications();
    }, []);


    const handleAddLanguage = (langCode) => {
        if (!editData.languages.includes(langCode)) {
            setEditData(prev => ({
                ...prev,
                languages: [...prev.languages, langCode]
            }));
        }
    };

    const handleRemoveLanguage = (langCode) => {
        setEditData(prev => ({
            ...prev,
            languages: prev.languages.filter((l) => l !== langCode)
        }));
    };

    const handleUpdate = async () => {
        const payload = {
            ...employee,
            ...editData,
            location: stringifyLocation(editData.location),
            qualifications: editData.qualifications.map((q) => ({
                name: q
            })),
        };

        try {
            console.log("🚀 Sende Employee-Payload:", payload);
            const response = await apiClient.put(`/api/employee/updateEmployee/${employee.id}`, payload);

            if (response.status === 200) {
                onUpdated?.(); // 🔁 Parent ruft neuen GET auf
                setIsFlipped(false); // zurückflippen
            } else {
                console.error('Fehler beim Speichern');
            }
        } catch (err) {
            console.error('Update-Fehler', err);
            const message = err?.response?.data || 'Überprüfen Sie die übermittelten Daten und versuchen Sie es erneut';
            setUpdateError(message);
        }
    };

    const handleDelete = async () => {
        try {
            const response = await apiClient.delete(`/api/employee/deleteEmployee/${employee.id}`);
            if (response.status === 200) {
                setShowDeleteModal(false);
                onUpdated?.();
            } else {
                setDeleteError('Unbekannter Fehler beim Löschen.');
            }
        } catch (err) {
            console.error('Lösch-Fehler:', err);
            const message = err?.response?.data || 'Fehler beim Löschen.';
            setDeleteError(message);
        }
    };

    const handleUpdateWithOverride = async (overrideData) => {
        const payload = {
            ...employee,
            ...overrideData,
            location: stringifyLocation(overrideData.location),
            qualifications: overrideData.qualifications.map((q) => ({name: q})),
        };

        try {
            const response = await apiClient.put(`/api/employee/updateEmployee/${employee.id}`, payload);
            if (response.status === 200) {
                onUpdated?.();
                setIsFlipped(false);
            } else {
                console.error('Fehler beim Speichern');
            }
        } catch (err) {
            console.error('Update-Fehler', err);
            const message = err?.response?.data || 'Überprüfen Sie die übermittelten Daten und versuchen Sie es erneut';
            setUpdateError(message);
        }
    };


    // ClickAway for the dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowLanguageSelect(false);
            }
        };

        if (showLanguageSelect) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showLanguageSelect]);

    useEffect(() => {
        if (showVacationModal) {
            const today = new Date().toISOString().split('T')[0];
            setNewVacation({
                startTime: `${today}T00:00`,
                endTime: `${today}T23:59`,
            });
        }
    }, [showVacationModal]);


    const instructorOptions = [];
    const mechanicOptions = [];
    const guestManagerOptions = [];

// 🧠 Wichtiger Teil: welche Suffixe sind erlaubt für diese Rolle?
    const allowedSuffixes = allowedQualificationGroupsByRole[employee.role] || [];

    allQualifications.forEach((q) => {
        const match = q.match(/^(.*?)(?:_(KL|M|GM))?$/);
        const base = (match && match[1]) || q;
        const suffix = (match && match[2]) || null;

        const alreadySelected = editData.qualifications.includes(q);
        if (alreadySelected) return;

        // 💡 Nur erlaubte Suffixe durchlassen
        if (!allowedSuffixes.includes(suffix)) return;

        if (!suffix || suffix === "KL") {
            const isKL = suffix === "KL";
            instructorOptions.push({
                label: isKL ? `${base} (KL)` : base,
                value: q,
            });

        } else if (suffix === "M") {
            mechanicOptions.push({label: base, value: q});
        } else if (suffix === "GM") {
            guestManagerOptions.push({label: base, value: q});
        }
    });

    instructorOptions.sort((a, b) => a.label.localeCompare(b.label, 'de'));
    mechanicOptions.sort((a, b) => a.label.localeCompare(b.label, 'de'));
    guestManagerOptions.sort((a, b) => a.label.localeCompare(b.label, 'de'));

    return (
        <div className="w-full">
            <div className="relative w-full h-[500px] [perspective:1000px]">
                <div
                    className={`relative w-full h-full transition-transform duration-700 [transform-style:preserve-3d] ${
                        isFlipped ? "[transform:rotateY(180deg)]" : ""
                    }`}
                >
                    {/* FRONT SIDE */}
                    <div
                        className="absolute w-full h-full [backface-visibility:hidden] bg-white shadow-md rounded-xl p-4 flex flex-col items-center space-y-3">
                        {employee.retired && (
                            <>
                                {/* Subtle blur overlay */}
                                <div
                                    className="absolute inset-0 bg-gray-400 bg-opacity-30 backdrop-blur-[1.5px] z-10 rounded-xl pointer-events-none"/>

                                {/* Diagonal text from corner to corner */}
                                <div
                                    className="absolute inset-0 z-20 pointer-events-none flex items-center justify-center">
                                      <span
                                          className="absolute left-1/2 top-1/2 text-white text-[64px] font-extrabold uppercase opacity-90 -translate-x-1/2 -translate-y-1/2 -rotate-[32deg] whitespace-nowrap drop-shadow-lg"
                                      >
                                        Retired
                                      </span>
                                </div>
                            </>
                        )}


                        <button
                            onClick={() => setIsFlipped(true)}
                            className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
                        >
                            <img src={Edit} alt="edit" className="h-5 w-5"/>
                        </button>

                        {/* Avatar, Name, Adresse */}
                        <div className="flex flex-col items-center text-center space-y-1 min-h-[80px] mb-4">
                            <img
                                src={
                                    editData.base64Image
                                        ? `data:image/jpeg;base64,${editData.base64Image}`
                                        : (employee.base64Image
                                            ? `data:image/jpeg;base64,${employee.base64Image}`
                                            : User)
                                }
                                alt="avatar"
                                className="h-16 w-16 rounded-full object-cover"
                            />
                            <h2 className="font-semibold text-lg text-gray-800">
                                {employee.firstName} {employee.lastName}
                            </h2>
                            <p className="text-sm text-gray-400 whitespace-pre-line">
                                {formatAddress(employee.location)}
                            </p>
                        </div>

                        {/* Overtime & Vacation */}
                        <div className="flex justify-center space-x-8 mt-2 min-h-[60px]">
                            <div className="text-center">
                                <img src={Clock} alt="clock" className="h-5 w-5 mx-auto" title="Überstunden"/>
                                <p
                                    className={`font-semibold ${
                                        employee.overtime < 0 ? "text-red-500" : "text-black"
                                    }`}
                                >
                                    {employee.overtime}
                                </p>
                            </div>
                            <div className="text-center">
                                <img src={Calendar} alt="monthly hours" className="h-5 w-5 mx-auto"
                                     title="Monatsstunden"/>
                                <p className="font-semibold text-black">{employee.monthlyHours}</p>
                            </div>
                            <div className="text-center">
                                <img src={Sun} alt="vacation" className="h-5 w-5 mx-auto" title="Urlaubstage"/>
                                <p className="font-semibold text-black">{employee.vacation}</p>
                            </div>
                        </div>

                        {/* Qualifications */}
                        <div className="w-full max-h-[96px] overflow-y-auto px-1">
                            <h3 className="text-sm font-medium text-gray-500 text-center border-b pb-1 mb-2">
                                Qualifikationen
                            </h3>
                            {employee.qualifications && employee.qualifications.length > 0 ? (
                                <div className="flex flex-wrap justify-center gap-2">
                                    {(() => {
                                        const uniqueBaseNames = new Map();
                                        employee.qualifications.forEach((q) => {
                                            const qName =
                                                typeof q === "object" && q.name
                                                    ? q.name
                                                    : typeof q === "string"
                                                        ? q
                                                        : "";
                                            if (!qName) return;
                                            const match = qName.match(/^(.*?)(?:_(KL|M|GM))?$/);
                                            const base = match?.[1] || qName;
                                            const suffix = match?.[2] || "DEFAULT";
                                            const priority = {KL: 3, M: 2, GM: 1, DEFAULT: 0};
                                            const current = uniqueBaseNames.get(base);
                                            if (!current || priority[suffix] > priority[current.suffix]) {
                                                uniqueBaseNames.set(base, {base, suffix});
                                            }
                                        });

                                        const colorMap = {
                                            KL: "bg-indigo-500 text-white",
                                            DEFAULT: "bg-indigo-100 text-indigo-800",
                                            M: "bg-emerald-200 text-emerald-900",
                                            GM: "bg-rose-200 text-rose-900",
                                        };

                                        return Array.from(uniqueBaseNames.values())
                                            .sort((a, b) => a.base.localeCompare(b.base, 'de'))
                                            .map(
                                                ({base, suffix}, idx) => (
                                                    <span
                                                        key={idx}
                                                        className={`text-xs px-3 py-1 rounded-full ${
                                                            colorMap[suffix] || colorMap.DEFAULT
                                                        }`}
                                                    >
                                                    {base}
                                                </span>
                                                )
                                            );
                                    })()}
                                </div>
                            ) : (
                                <p className="text-sm text-gray-400 text-center">No qualifications</p>
                            )}
                        </div>

                        {/* Languages (Front Side) */}
                        <div className="w-full mt-auto pt-4">
                            <h3 className="text-sm font-medium text-gray-500 text-center border-b pb-1 mb-2">
                                Sprachen
                            </h3>
                            {/*
                                If you want the front side to also show updated languages,
                                use `languages` instead of `employee.languages`.
                            */}
                            <div className="flex justify-center space-x-3 text-xl">
                                {editData.languages.map((lang, idx) => (
                                    <span key={idx}>{languageFlags[lang] || lang}</span>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* BACK SIDE */}
                    <div
                        className="absolute w-full h-full [backface-visibility:hidden] [transform:rotateY(180deg)] bg-white shadow-md rounded-xl p-4 flex flex-col justify-between">
                        {/* Inhalt oben */}
                        <button
                            onClick={() => {
                                setEditData({
                                    firstName: employee.firstName,
                                    lastName: employee.lastName,
                                    overtime: employee.overtime,
                                    vacation: employee.vacation,
                                    blockedTime: employee.blockedTime || [],
                                    vacationDays: employee.vacationDays || [],
                                    languages: employee.languages || [],
                                    qualifications: (employee.qualifications || []).map((q) =>
                                        typeof q === 'string' ? q : q?.name || ''
                                    ),
                                    location: typeof employee.location === 'string'
                                        ? parseAddressString(employee)
                                        : employee.location || {
                                        street: '', streetNumber: '', postalCode: '', city: '', country: ''
                                    },
                                });
                                setIsFlipped(false)
                            }}
                            className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
                        >
                            <img
                                src={Plus}
                                alt="speichern"
                                className="h-5 w-5 pointer-events-none rotate-45"
                            />
                        </button>
                        <div>
                            <div className="flex flex-col space-y-4">
                                {/* Vorname + Nachname */}
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Profilbild hochladen</label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageUpload}
                                        className="text-sm"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label
                                            htmlFor="firstName"
                                            className="block text-sm font-medium text-gray-700 mb-1"
                                        >
                                            Vorname
                                        </label>
                                        <input
                                            id="firstName"
                                            name="firstName"
                                            type="text"
                                            value={editData.firstName}
                                            onChange={(e) =>
                                                setEditData((prev) => ({...prev, firstName: e.target.value}))
                                            }
                                            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-1 text-sm text-gray-800 placeholder-gray-400 focus:border-indigo-500 focus:ring-0 transition"
                                        />
                                    </div>

                                    <div>
                                        <label
                                            htmlFor="lastName"
                                            className="block text-sm font-medium text-gray-700 mb-1"
                                        >
                                            Nachname
                                        </label>
                                        <input
                                            id="lastName"
                                            name="lastName"
                                            type="text"
                                            value={editData.lastName}
                                            onChange={(e) =>
                                                setEditData((prev) => ({...prev, lastName: e.target.value}))
                                            }
                                            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-1 text-sm text-gray-800 placeholder-gray-400 focus:border-indigo-500 focus:ring-0 transition"
                                        />
                                    </div>
                                </div>

                                {/* Adresse */}
                                <div className="flex items-center text-sm text-gray-700 space-x-2 mb-4">
                                    <button
                                        onClick={() => setShowAddressModal(true)}
                                        className="flex items-center text-sm text-indigo-600 underline hover:text-indigo-800 gap-2 max-w-full"
                                    >
                                        <img src={PinIcon} alt="Ort" className="h-4 w-4"/>
                                        <span className="truncate block text-left">
                                            {[
                                                editData.location?.street,
                                                editData.location?.streetNumber,
                                                editData.location?.postalCode,
                                                editData.location?.city,
                                                editData.location?.country,
                                            ]
                                                .filter(Boolean)
                                                .join(', ') || 'Adresse bearbeiten'}
                                        </span>
                                    </button>
                                </div>
                            </div>

                            {/* Overtime + Vacation */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="flex items-center space-x-2">
                                    <img src={Clock} alt="Überstunden" className="h-5 w-5" title="Überstunden"/>
                                    <div className="w-full">
                                        <input
                                            id="overtime"
                                            name="overtime"
                                            type="text"
                                            value={editData.overtime}
                                            onChange={(e) =>
                                                setEditData((prev) => ({...prev, overtime: e.target.value}))
                                            }
                                            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-1 text-sm text-gray-800 placeholder-gray-400 focus:border-indigo-500 focus:ring-0 transition"
                                        />
                                    </div>
                                </div>

                                <div className="flex items-center space-x-2">
                                    <img src={Calendar} alt="Monatsstunden" className="h-5 w-5" title="Monatsstunden"/>
                                    <div className="w-full">
                                        <input
                                            id="monthlyHours"
                                            name="monthlyHours"
                                            type="text"
                                            value={editData.monthlyHours}
                                            onChange={(e) =>
                                                setEditData((prev) => ({...prev, monthlyHours: e.target.value}))
                                            }
                                            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-1 text-sm text-gray-800 placeholder-gray-400 focus:border-indigo-500 focus:ring-0 transition"
                                        />
                                    </div>
                                </div>

                                <div className="flex items-center space-x-2">
                                    <img src={Sun} alt="Urlaubstage" className="h-5 w-5" title="Urlaubstage"/>
                                    <div className="w-full">
                                        <input
                                            id="vacation"
                                            name="vacation"
                                            type="text"
                                            value={editData.vacation}
                                            onChange={(e) =>
                                                setEditData((prev) => ({...prev, vacation: e.target.value}))
                                            }
                                            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-1 text-sm text-gray-800 placeholder-gray-400 focus:border-indigo-500 focus:ring-0 transition"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div>
                                <button
                                    onClick={() => setShowVacationModal(true)}
                                    className="text-indigo-600 text-xs underline hover:text-indigo-800 ml-7"
                                >
                                    + Urlaubszeitraum hinzufügen
                                </button>
                            </div>

                            <div className="mt-4">
                                <div
                                    onClick={() => setShowQualificationModal(true)}
                                    className="relative flex items-center gap-2 overflow-x-auto max-w-full pr-6 cursor-pointer group flex-nowrap"
                                >
                                    {(() => {
                                        const uniqueBaseNames = new Map();
                                        (editData.qualifications || []).forEach((q) => {
                                            const match = q.match(/^(.*?)(?:_(KL|M|GM))?$/);
                                            const base = (match && match[1]) || q;
                                            const suffix = (match && match[2]) || null;
                                            const priority = {KL: 3, M: 2, GM: 1, DEFAULT: 0};
                                            const current = uniqueBaseNames.get(base);
                                            if (!current || priority[suffix] > priority[current.suffix]) {
                                                uniqueBaseNames.set(base, {full: q, base, suffix});
                                            }
                                        });

                                        const colorMap = {
                                            KL: "bg-indigo-500 text-white",
                                            DEFAULT: "bg-indigo-100 text-indigo-800",
                                            M: "bg-emerald-200 text-emerald-900",
                                            GM: "bg-rose-200 text-rose-900",
                                        };


                                        const pills = Array.from(uniqueBaseNames.values());
                                        const visibleCount = Math.min(pills.length, 2);
                                        const visiblePills = pills.slice(0, visibleCount);

                                        const displayPills = [...visiblePills, {base: "...", suffix: "DEFAULT"}];

                                        return displayPills.map(({base, suffix}, idx) => (
                                            <span
                                                key={idx}
                                                className={`text-xs px-3 py-1 rounded-full whitespace-nowrap transition-opacity ${
                                                    colorMap[suffix] || colorMap.DEFAULT
                                                }`}
                                                title="Klicken zum Bearbeiten"
                                            >
                                                {base}
                                            </span>
                                        ));
                                    })()}

                                    {editData.qualifications.length > 3 && (
                                        <div
                                            className="absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-r from-transparent to-white pointer-events-none rounded-r"></div>
                                    )}
                                </div>
                            </div>
                            {/* LANGUAGES (Back Side) */}
                            <div className="mt-4">
                                <div className="flex flex-wrap items-center gap-2">
                                    {/* Languages */}
                                    {editData.languages.map((lang) => (
                                        <button
                                            key={lang}
                                            onClick={() => handleRemoveLanguage(lang)}
                                            className="flex items-center space-x-1 bg-indigo-100 text-indigo-800 text-sm px-2 py-1 rounded-full hover:bg-red-100 hover:text-red-600 transition"
                                            title="Klicken zum Entfernen"
                                        >
                                            <span>{languageFlags[lang] || lang}</span>
                                            <span className="text-xs font-bold">&times;</span>
                                        </button>
                                    ))}

                                    {/* Plus Button – same line */}
                                    <div className="relative" ref={dropdownRef}>
                                        <button
                                            type="button"
                                            onClick={() => setShowLanguageSelect((prev) => !prev)}
                                            className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-200 text-gray-600 hover:bg-indigo-500 hover:text-white transition leading-none text-base font-medium"
                                            title="Sprache hinzufügen"
                                        >
                                            <span className="-mt-[1px]">+</span>
                                        </button>

                                        {showLanguageSelect && (
                                            <div
                                                className="absolute bottom-full left-0 mb-2 z-10 bg-white border border-gray-200 rounded-md shadow-lg p-2 max-h-48 overflow-y-auto w-48"
                                            >
                                                {Object.keys(languageFlags)
                                                    .filter((lang) => !editData.languages.includes(lang))
                                                    .map((lang) => (
                                                        <button
                                                            key={lang}
                                                            onClick={() => {
                                                                handleAddLanguage(lang);
                                                                setShowLanguageSelect(false);
                                                            }}
                                                            className="w-full text-left text-sm px-2 py-1 rounded hover:bg-indigo-100 hover:text-indigo-800 transition"
                                                        >
                                                            {languageFlags[lang]} {lang}
                                                        </button>
                                                    ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                        </div>

                        {/* Buttons unten */}
                        {updateError && (
                            <div className="text-sm text-red-600 text-center mt-2">
                                {updateError}
                            </div>
                        )}
                        <div className="flex justify-center space-x-4 mt-6">
                            {/* Retired-Button */}
                            <button
                                type="button"
                                onClick={() => {
                                    const updatedData = {
                                        ...editData,
                                        retired: !editData.retired,
                                    };

                                    setEditData(updatedData);
                                    handleUpdateWithOverride(updatedData);
                                }}
                                className={`rounded-full px-4 py-2 text-xs text-white shadow-md ${
                                    employee.retired
                                        ? "bg-green-500 hover:bg-green-400"
                                        : "bg-red-500 hover:bg-red-400"
                                }`}
                                title={employee.retired ? "Wiedereinstellen" : "Mitarbeiter deaktivieren"}
                            >
                                <img
                                    src={employee.retired ? Employ : Retire}
                                    alt={employee.retired ? "Wiedereinstellen" : "Retired setzen"}
                                    className="h-5 w-5 pointer-events-none"
                                />
                            </button>

                            {/* Löschen Button */}
                            <button
                                type="button"
                                className={`rounded-full px-4 py-2 text-xs text-white shadow-md focus-visible:outline-2 focus-visible:outline-offset-2 bg-indigo-600 hover:bg-indigo-500 focus-visible:outline-indigo-600"}
                                `}
                                onClick={() => setShowDeleteModal(true)}
                            >
                                <img src={Trash} alt="löschen" className="h-5 w-5"/>
                            </button>


                            {/* Speichern Button */}
                            <button
                                type="button"
                                onClick={handleUpdate}
                                className="rounded-full bg-indigo-400 px-4 py-2 text-xs text-white shadow-md hover:bg-indigo-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-400"
                            >
                                <img
                                    src={Check}
                                    alt="speichern"
                                    className="h-5 w-5 pointer-events-none"
                                />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* MODAL for address */}
            {showAddressModal && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-white/10 transition-opacity duration-300 animate-fadeIn">
                    <div
                        className="relative bg-white rounded-xl p-6 w-[90%] max-w-md shadow-xl border border-gray-200 transform transition-all duration-300 animate-scaleIn">
                        <h2 className="text-lg font-semibold text-gray-800 mb-4">Adresse bearbeiten</h2>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs text-gray-500 mb-1">Straße</label>
                                <input
                                    value={editData.location.street}
                                    onChange={(e) =>
                                        setEditData((prev) => ({
                                            ...prev,
                                            location: {...prev.location, street: e.target.value},
                                        }))
                                    }
                                    className="w-full rounded-md border border-gray-200 px-3 py-1.5 text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-500 mb-1">Nr.</label>
                                <input
                                    value={editData.location.streetNumber}
                                    onChange={(e) =>
                                        setEditData((prev) => ({
                                            ...prev,
                                            location: {...prev.location, streetNumber: e.target.value},
                                        }))
                                    }
                                    className="w-full rounded-md border border-gray-200 px-3 py-1.5 text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-500 mb-1">PLZ</label>
                                <input
                                    value={editData.location.postalCode}
                                    onChange={(e) =>
                                        setEditData((prev) => ({
                                            ...prev,
                                            location: {...prev.location, postalCode: e.target.value},
                                        }))
                                    }
                                    className="w-full rounded-md border border-gray-200 px-3 py-1.5 text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-500 mb-1">Stadt</label>
                                <input
                                    value={editData.location.city}
                                    onChange={(e) =>
                                        setEditData((prev) => ({
                                            ...prev,
                                            location: {...prev.location, city: e.target.value},
                                        }))
                                    }
                                    className="w-full rounded-md border border-gray-200 px-3 py-1.5 text-sm"
                                />
                            </div>
                            <div className="col-span-2">
                                <label className="block text-xs text-gray-500 mb-1">Land</label>
                                <input
                                    value={editData.location.country}
                                    onChange={(e) =>
                                        setEditData((prev) => ({
                                            ...prev,
                                            location: {...prev.location, country: e.target.value},
                                        }))
                                    }
                                    className="w-full rounded-md border border-gray-200 px-3 py-1.5 text-sm"
                                />
                            </div>
                        </div>

                        <div className="mt-6 flex justify-end space-x-3">
                            <button
                                onClick={() => setShowAddressModal(false)}
                                className="px-4 py-2 text-sm bg-gray-200 rounded-md hover:bg-gray-300"
                            >
                                Abbrechen
                            </button>
                            <button
                                onClick={() => {
                                    // speichern kommt später
                                    setShowAddressModal(false);
                                }}
                                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-500"
                            >
                                Speichern
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {showQualificationModal && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-white/10 transition-opacity duration-300 animate-fadeIn">
                    <div
                        className="relative bg-white rounded-xl p-6 w-[90%] max-w-md shadow-xl border border-gray-200 transform transition-all duration-300 animate-scaleIn">
                        <h2 className="text-lg font-semibold text-gray-800 mb-4">Qualifikationen bearbeiten</h2>

                        <div className="flex flex-wrap gap-2 mb-4 max-h-[150px] overflow-y-auto pr-1">
                            {(() => {
                                const uniqueBaseNames = new Map();
                                (editData.qualifications || []).forEach((q) => {
                                    const match = q.match(/^(.*?)(?:_(KL|M|GM))?$/);
                                    const base = (match && match[1]) || q;
                                    const suffix = (match && match[2]) || null;
                                    const priority = {KL: 3, M: 2, GM: 1, DEFAULT: 0};
                                    const current = uniqueBaseNames.get(base);
                                    if (!current || priority[suffix] > priority[current.suffix]) {
                                        uniqueBaseNames.set(base, {full: q, base, suffix});
                                    }
                                });

                                const colorMap = {
                                    KL: "bg-indigo-500 text-white",
                                    DEFAULT: "bg-indigo-100 text-indigo-800",
                                    M: "bg-emerald-200 text-emerald-900",
                                    GM: "bg-rose-200 text-rose-900",
                                };

                                return Array.from(uniqueBaseNames.values())
                                    .sort((a, b) => a.base.localeCompare(b.base, 'de'))
                                    .map(({full, base, suffix}, idx) => (
                                        <div
                                            key={idx}
                                            className={`flex items-center gap-1 text-xs px-3 py-1 rounded-full ${colorMap[suffix] || colorMap.DEFAULT}`}
                                        >
                                            <span>{base}</span>
                                            <button
                                                onClick={() =>
                                                    setEditData(prev => ({
                                                        ...prev,
                                                        qualifications: prev.qualifications.filter((q) => q !== full),
                                                    }))
                                                }
                                                className="ml-1 text-xs font-bold hover:text-red-600 transition"
                                                title="Entfernen"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ));
                            })()}

                        </div>

                        {/* Dropdown for adding */}
                        <div className="flex flex-col gap-2">
                            <select
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                                onChange={(e) => {
                                    const selected = e.target.value;
                                    if (selected && !editData.qualifications.includes(selected)) {
                                        setEditData(prev => ({
                                            ...prev,
                                            qualifications: [...prev.qualifications, selected],
                                        }));
                                    }
                                    e.target.value = "";
                                }}
                            >
                                <option value="">+ Qualifikation hinzufügen</option>

                                {instructorOptions.length > 0 && (
                                    <optgroup label="Instruktoren">
                                        {instructorOptions.map((opt) => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </optgroup>
                                )}

                                {mechanicOptions.length > 0 && (
                                    <optgroup label="Mechaniker">
                                        {mechanicOptions.map((opt) => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </optgroup>
                                )}

                                {guestManagerOptions.length > 0 && (
                                    <optgroup label="Guestmanager">
                                        {guestManagerOptions.map((opt) => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </optgroup>
                                )}
                            </select>


                            {/* Custom entry */}
                            <div className="flex items-center gap-2">
                                {/* Qualifikations-Input */}
                                <input
                                    type="text"
                                    value={qualificationInput}
                                    onChange={(e) => setQualificationInput(e.target.value)}
                                    placeholder="Neue Qualifikation eingeben..."
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" && qualificationInput.trim()) {
                                            let newQ = qualificationInput.trim();
                                            if (newQualificationSuffix) {
                                                newQ = `${newQ}_${newQualificationSuffix}`;
                                            }

                                            if (!editData.qualifications.includes(newQ)) {
                                                setEditData(prev => ({
                                                    ...prev,
                                                    qualifications: [...prev.qualifications, newQ],
                                                }));
                                            }

                                            setQualificationInput('');
                                            setNewQualificationSuffix('');
                                        }
                                    }}
                                    className={`flex-1 rounded-md border px-3 py-2 text-sm transition${
                                        newQualificationSuffix === 'KL'
                                            ? 'border-indigo-500 bg-indigo-50 text-indigo-900'
                                            : newQualificationSuffix === 'M'
                                                ? 'border-emerald-400 bg-emerald-50 text-emerald-900'
                                                : newQualificationSuffix === 'GM'
                                                    ? 'border-rose-400 bg-rose-50 text-rose-900'
                                                    : 'border-indigo-200 bg-indigo-100 text-indigo-800'
                                    }`}
                                />

                                {/* Gruppe-Auswahl */}
                                <select
                                    value={newQualificationSuffix}
                                    onChange={(e) => setNewQualificationSuffix(e.target.value)}
                                    className="text-xs rounded-md border border-gray-300 px-2 py-[6px] bg-white text-gray-700"
                                >
                                    <option value="">Instruktor:in</option>
                                    <option value="KL">Kursleiter:in</option>
                                    <option value="M">Mechaniker:in</option>
                                    <option value="GM">Guestmanager</option>
                                </select>
                            </div>
                        </div>

                        <div className="mt-6 flex justify-end space-x-3">
                            <button
                                onClick={() => {
                                    setEditData((prev) => ({
                                        ...prev,
                                        qualifications: (employee.qualifications || []).map((q) =>
                                            typeof q === 'string' ? q : q?.name || ''
                                        ),
                                    }));
                                    setShowQualificationModal(false);
                                }}
                                className="px-4 py-2 text-sm bg-gray-200 rounded-md hover:bg-gray-300"
                            >
                                Abbrechen
                            </button>
                            <button
                                onClick={() => setShowQualificationModal(false)}
                                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-500"
                            >
                                Speichern
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {showVacationModal && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-white/10 transition-opacity duration-300 animate-fadeIn">
                    <div
                        className="relative bg-white rounded-xl p-6 w-[90%] max-w-md shadow-xl border border-gray-200 transform transition-all duration-300 animate-scaleIn">
                        <h2 className="text-lg font-semibold text-gray-800 mb-4">Urlaubszeit hinzufügen</h2>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm text-gray-700 mb-1">Von</label>
                                <input
                                    type="datetime-local"
                                    value={newVacation.startTime}
                                    onChange={(e) =>
                                        setNewVacation((prev) => ({...prev, startTime: e.target.value}))
                                    }
                                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                                />
                            </div>

                            <div>
                                <label className="block text-sm text-gray-700 mb-1">Bis</label>
                                <input
                                    type="datetime-local"
                                    value={newVacation.endTime}
                                    onChange={(e) =>
                                        setNewVacation((prev) => ({...prev, endTime: e.target.value}))
                                    }
                                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                                />
                            </div>
                            <button
                                onClick={() => {
                                    if (newVacation.startTime && newVacation.endTime) {
                                        setEditData((prev) => ({
                                            ...prev,
                                            vacationDays: [
                                                ...prev.vacationDays,
                                                {
                                                    startTime: newVacation.startTime,
                                                    endTime: newVacation.endTime,
                                                },
                                            ],
                                        }));

                                        // ⏰ Reset auf aktuellen Tag mit 00:00 / 23:59
                                        const today = new Date().toISOString().split('T')[0];
                                        setNewVacation({
                                            startTime: `${today}T00:00`,
                                            endTime: `${today}T23:59`
                                        });
                                    }
                                }}
                                className="px-4 py-2 text-xs bg-indigo-100 text-indigo-800 rounded-md hover:bg-indigo-200"
                            >
                                Zeitraum hinzufügen
                            </button>


                            {/* Optional: Bestehende Urlaube anzeigen */}
                            {editData.vacationDays.length > 0 && (
                                <div>
                                    <h4 className="text-sm font-medium text-gray-600 mb-2">Bereits geplant</h4>
                                    <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto">
                                        {editData.vacationDays.map((t, idx) => (
                                            <div
                                                key={idx}
                                                className="flex items-center bg-indigo-100 text-indigo-800 text-xs px-3 py-1 rounded-full"
                                            >
                                                {formatLocalDateTime(t.startTime)} – {formatLocalDateTime(t.endTime)}
                                                <button
                                                    onClick={() =>
                                                        setEditData((prev) => ({
                                                            ...prev,
                                                            vacationDays: prev.vacationDays.filter((_, i) => i !== idx)
                                                        }))
                                                    }
                                                    title="Entfernen"
                                                    className="ml-2 text-red-500 hover:text-red-700 font-bold"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))}
                                    </div>

                                </div>
                            )}
                        </div>

                        <div className="mt-6 flex justify-end space-x-3">
                            <button
                                onClick={() => setShowVacationModal(false)}
                                className="px-4 py-2 text-sm bg-gray-200 rounded-md hover:bg-gray-300"
                            >
                                Abbrechen
                            </button>
                            <button
                                onClick={() => setShowVacationModal(false)}
                                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-500"
                            >
                                Speichern
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {showDeleteModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-white/10">
                    <div className="bg-white rounded-xl p-6 w-[90%] max-w-sm shadow-xl border border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-800 mb-4">Mitarbeiter löschen?</h2>
                        <p className="text-sm text-gray-600 mb-4">
                            Möchtest du <strong>{employee.firstName} {employee.lastName}</strong> wirklich löschen?
                            Diese Aktion kann nicht rückgängig gemacht werden.
                        </p>
                        {deleteError && (
                            <p className="text-sm text-red-600 mb-4">
                                {deleteError}
                            </p>
                        )}

                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setDeleteError(''); // Reset on close
                                }}
                                className="px-4 py-2 text-sm bg-gray-200 rounded-md hover:bg-gray-300"
                            >
                                Abbrechen
                            </button>
                            <button
                                onClick={handleDelete}
                                className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-500"
                            >
                                Löschen
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmployeeCard;
